/**
 * Execution Controls Hook
 * Handles pause, resume, and cancel operations
 * Extracted from TopToolbar to follow SRP
 */

import { useCallback } from 'react';
import { useDesignerModeStore } from '@flow-studio/store';
import { processEngineExecutionApiClient } from '@flow-studio/api';
import { useExecutionNotifications } from './useExecutionNotifications';

/**
 * Hook providing execution control handlers
 * Pause, resume, and cancel execution
 */
export const useExecutionControls = () => {
  const currentExecutionId = useDesignerModeStore((s) => s.currentExecutionId);
  const setMode = useDesignerModeStore((s) => s.setMode);
  const notifications = useExecutionNotifications();

  /**
   * Pause running execution
   */
  const handlePause = useCallback(async () => {
    if (!currentExecutionId?.ResID) {
      notifications.notifyExecutionError(
        'No active execution',
        'Cannot pause'
      );
      return;
    }

    try {
      const response = await processEngineExecutionApiClient.pauseExecution(
        currentExecutionId  // Pass full ExecutionIdentifier with ResID and ID
      );

      if (response.success) {
        notifications.notifyExecutionPaused();
      } else {
        // "NotFound", "Operation", or "Execution" mean the execution is no longer
        // active — treat as already done rather than a user-facing failure.
        const code = response.error?.code || '';
        if (code === 'NotFound' || code === 'Operation' || code === 'Execution') {
          notifications.notifyExecutionCompleted('completed');
          setMode('design');
        } else {
          const error = response.error?.message || 'Unknown error';
          notifications.notifyExecutionError(error, 'Failed to pause');
        }
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Unknown error';
      notifications.notifyExecutionError(errorMsg, 'Pause error');
    }
  }, [currentExecutionId, setMode, notifications]);

  /**
   * Resume paused execution
   */
  const handleResume = useCallback(async () => {
    if (!currentExecutionId?.ResID) {
      notifications.notifyExecutionError(
        'No active execution',
        'Cannot resume'
      );
      return;
    }

    try {
      const response = await processEngineExecutionApiClient.resumeExecution(
        currentExecutionId  // Pass full ExecutionIdentifier with ResID and ID
      );

      if (response.success) {
        notifications.notifyExecutionResumed();
      } else {
        const error = response.error?.message || 'Unknown error';
        notifications.notifyExecutionError(error, 'Failed to resume');
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Unknown error';
      notifications.notifyExecutionError(errorMsg, 'Resume error');
    }
  }, [currentExecutionId, notifications]);

  /**
   * Cancel running execution
   */
  const handleCancel = useCallback(async () => {
    if (!currentExecutionId?.ResID) {
      notifications.notifyExecutionError(
        'No active execution',
        'Cannot cancel'
      );
      return;
    }

    try {
      const response = await processEngineExecutionApiClient.cancelExecution(
        currentExecutionId  // Pass full ExecutionIdentifier with ResID and ID
      );

      if (response.success) {
        notifications.notifyExecutionCancelled();
      } else {
        // "NotFound" or "Operation" mean the execution is no longer active —
        // treat as already cancelled rather than a user-facing failure.
        const code = response.error?.code || '';
        if (code === 'NotFound' || code === 'Operation') {
          notifications.notifyExecutionCancelled();
        } else {
          const error = response.error?.message || 'Unknown error';
          notifications.notifyExecutionError(error, 'Failed to cancel');
        }
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Unknown error';
      notifications.notifyExecutionError(errorMsg, 'Cancel error');
    } finally {
      // Always exit execution mode when cancel is attempted —
      // the user's intent is to stop regardless of API outcome.
      setMode('design');
    }
  }, [currentExecutionId, setMode, notifications]);

  return {
    handlePause,
    handleResume,
    handleCancel,
  };
};
